import React, { useContext } from 'react';
import * as Yup from 'yup';
import { Form } from 'formik';
import { Grid, Typography, Link } from '@material-ui/core';
import { Carrier, FormInputs, getCarriers } from '../AgentResources';
import { Button, ButtonProps, FormikValues, FormWrap, Label, SelectField } from '../formikFormInputs';
import { SnackbarContext } from '../Snackbar/Context';
import { GeneralDialog, GenericDialogProps } from '../dialog';
import { Viewer } from '../Common';
import { privateApi } from '../../lib/privateApi';
import { errorLog, handleFormError, mc, noop, numArray } from '../../utils/commonUtils';
import { genderDropDown, initFalse, lowerAgeLimit, upperAgeLimit } from '../../utils/constants';
import { SnackBarSeverity } from '../../utils/enums';
import { REQUIRED } from '../../utils/validation';
import { carrierId, stagingServerUrl } from '../../server/constants';


interface IllustrationForm extends FormikValues {
  errorMessage: string;
  setErrorMessage: Function;
}

type IllustrationFilters = {
  carrier: number;
  contribution: number;
  gender: string;
  issueage: number;
  rateclass: string;
}

enum NLFieldsEnum {
  Age = 'issueage',
  Contribution = 'contribution',
  Gender = 'gender',
}

const SampleNLForm = (props: IllustrationForm) => {
  const getMaleFemale = (gen: string) => gen === 'Male' ? 1 : 2;
  const {errors, handleBlur, handleChange, isSubmitting, isValid, setFieldValue, touched, values} = props;
  // National_Life_Carrier_Id
  const isNL = values.carrierId === carrierId;
  const disableButton = (values.carrierId === '') ? 'disable' : '';
  const [carriers, setCarriers]: [Carrier[], any] = React.useState([]);
  const [minContribution, setMinContribution] = React.useState<number>(24000);
  const [_startCAmt, maxCAmt, contributionAmtInc] = [minContribution, 250000, 2000];
  const [contributionOpn, setContributionOpn] = React.useState<any[]>([]);
  const allFields = [
    {
      component: SelectField,
      label: <Label label={'Select a Carrier'} required />,
      name: 'carrierId',
      onSelectChange: noop,
      options: carriers.map(c => ({key: c.id, name: c.carrierName, value: c})),
      rowSize: 12,
    }
  ];

  const snackbar = useContext(SnackbarContext);
  const calculateMinContributionEstimate: (payload: any) => Promise<any> = async (payload: any) => {
    const [url, config] = [`${stagingServerUrl}/estimation/getMinimumContribution`, {params: payload}];
    return privateApi.get(url, config)
      .then(response => response.data)
      .catch((err: any) => {
        const errorMsg: any = handleFormError(err);
        snackbar.showMessage(errorMsg, SnackBarSeverity.Error);
      });
  };
  const validateContribution = async () => {
    if (!values.issueage || !values.gender) {
      return;
    }

    const _payload = {
      estimationType: 'QUICK',
      gender: getMaleFemale(values.gender),
      issueAge: values.issueage,
      rateClass: 'Standard_NT'
    };

    const res: {minimumContribution: number} = await calculateMinContributionEstimate(_payload);
    const minValue: number = res.minimumContribution + (2000 - res.minimumContribution % 2000);
    const _startCAmt = res ? minValue : 24000
    setMinContribution(_startCAmt);
    setContributionOpn(numArray(maxCAmt, _startCAmt, contributionAmtInc).map(key => ({key, name: mc(key.toString())})))
  };
  const nlFields = [
    {
      component: SelectField,
      label: <Label label={'Select Age'} required />,
      name: NLFieldsEnum.Age,
      onSelectChange: noop,
      options: numArray(upperAgeLimit, lowerAgeLimit).map(key => ({key, name: key})),
      rowSize: 4,
    },
    {
      component: SelectField,
      label: <Label label={'Select Gender'} required />,
      name: NLFieldsEnum.Gender,
      onSelectChange: noop,
      options: genderDropDown,
      rowSize: 4,
    },
    {
      component: SelectField,
      disabled: !values.issueage || !values.gender,
      label: <Label label={'Annual Contribution'} required />,
      name: NLFieldsEnum.Contribution,
      onSelectChange: noop,
      options: contributionOpn,
      rowSize: 4,
    }
  ];
  const eff = () => {
    getCarriers()
      .then(setCarriers);
  };

  const commonButtProps: ButtonProps = {
    className: ' mt20 mb20 linkBtn ' + disableButton,
    color: 'primary',
    label: 'View Sample Illustration',
    variant: 'contained',
  };
  const buttProps: any = isNL ? {
    ...commonButtProps,
    disabled: isSubmitting || !isValid || !values[NLFieldsEnum.Contribution],
    type: 'submit',
  } : {
    ...commonButtProps,
    href: 'https://www.dropbox.com/sh/rj4xyo81fiao84o/AACWr7v4C5XCKE8OG9pgsddYa?dl=0',
    target: '__blank',
  };

  React.useEffect(eff, []);

  React.useEffect(() => {
    validateContribution().then(() => setFieldValue(NLFieldsEnum.Contribution, ''));
  }, [values.gender, values.issueage]);

  return (
    <Form className="mt20">
      <Grid container spacing={3}>
        <FormInputs
          fields={isNL ? [...allFields, ...nlFields] : allFields}
          {...{errors, values, touched, handleChange, handleBlur}}
        />
      </Grid>

      <Button {...buttProps} />

      <Typography className="mb20" variant="body1">
        For custom illustration requests please fill out the <Link className="itemHover" href="https://na3.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhCab5B7v_9idZIRj8bRoDaSR5P3A6itjFgDK4hN7xxU5JFb-UfN_Ex4rjsMcmWgTIQ*" target="_blank">illustration request form</Link>.  Please allow up to 48 hours for a response.
      </Typography>
    </Form>
  );
}

const SampleIllustrations: React.FC = () => {
  const searchIllustration = ({contribution, gender, issueage}: IllustrationFilters) => {
    return privateApi.get('illustration/search', {params: {contribution, gender, issueage, rateclass: 'Standard_NT'}})
      .then(res => res.data)
      .catch(errorLog);
  };
  const [illustrationData, setIllustrationData] = React.useState<any>(null);
  const snackbar = useContext(SnackbarContext)

  const submit = async (payload: IllustrationFilters) => {
    const data: any = await searchIllustration(payload);

    if (!data || !data.illustration) {
      snackbar.showMessage(data.errorMessage || 'Illustration not found', SnackBarSeverity.Error)
      return;
    }

    setIllustrationData(data.illustration);
  };

  const _valSchema = {
    issueage: Yup.number().required(REQUIRED).min(lowerAgeLimit).max(upperAgeLimit),
    carrierId: Yup.number().required(REQUIRED),
    contribution: Yup.string().required(REQUIRED),
    gender: Yup.string().required(REQUIRED),
  };
  const fProps: any = {
    children: SampleNLForm,
    initialValues: {issueage: '', carrierId: '', contribution: '', gender: ''},
    onSubmit: submit,
    validationSchema: Yup.object(_valSchema)
  };
  const dialogProps: GenericDialogProps = {
    content: (<Viewer title={'NL Sample Illustration'} url={illustrationData} />),
    fullWidth: !initFalse,
    maxWidth: 'lg',
    dividers: !initFalse,
    onClose: () => {
      setIllustrationData(null);
    },
    open: !!illustrationData,
    title: 'Sample Illustration'
  };

  return (
    <>
      <FormWrap {...fProps} />
      <GeneralDialog  {...dialogProps} />
    </>
  );
};

export default SampleIllustrations;
